import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Modal from '../components/UI/Modal';

const IND_STATES = ['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Andaman and Nicobar','Chandigarh','Dadra and Nagar Haveli','Daman and Diu','Delhi','Jammu and Kashmir','Ladakh','Lakshadweep','Puducherry'];

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState({ page: 1, total: 0, total_pages: 0 });
  const [filter, setFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [companyState, setCompanyState] = useState('');
  const [form, setForm] = useState({ marketplace_account_id: '', marketplace_order_id: '', customer_name: '', customer_email: '', customer_phone: '', shipping_address: '', shipping_city: '', shipping_state: '', status: 'pending', payment_status: 'pending', order_date: new Date().toISOString().split('T')[0] });
  const [items, setItems] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [platforms, setPlatforms] = useState([]);

  const fetchOrders = async (p = 1) => {
    try {
      setLoading(true);
      const url = `/orders/list.php?page=${p}&limit=50${filter ? '&marketplace=' + filter : ''}`;
      const res = await api.get(url);
      if (res.data.success) {
        setOrders(res.data.data.orders || []);
        setPage({ page: res.data.data.page, total: res.data.data.total, total_pages: res.data.data.total_pages });
      } else setError(res.data.message || 'Failed to fetch');
    } catch (e) { setError('Failed to load orders'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchOrders();
    api.get('/settings/company.php').then(r => {
      if (r.data.success) setCompanyState(r.data.data?.company_state || 'Maharashtra');
    }).catch(() => setCompanyState('Maharashtra'));
    api.get('/products/list.php?limit=1000').then(r => {
      if (r.data.success) setProducts(r.data.data.products || []);
    }).catch(() => {});
    api.get('/platforms/list.php').then(r => {
      if (r.data.success) setPlatforms(r.data.data || []);
    }).catch(() => {});
  }, [filter]);

  const calcItemGst = (totalPrice, taxRate, state) => {
    if (!totalPrice || !taxRate || !state || !companyState) return { cgst: 0, sgst: 0, igst: 0, tax: 0, taxable: 0 };
    const amt = parseFloat(totalPrice) || 0;
    const rate = parseFloat(taxRate) || 0;
    const taxable = +(amt / (1 + rate / 100)).toFixed(2);
    if (state === companyState) {
      const half = +(taxable * (rate / 2) / 100).toFixed(2);
      return { cgst: half, sgst: half, igst: 0, tax: +(half * 2).toFixed(2), taxable };
    } else {
      const i = +(taxable * rate / 100).toFixed(2);
      return { cgst: 0, sgst: 0, igst: i, tax: i, taxable };
    }
  };

  const totalAmount = items.reduce((s, it) => s + (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 0), 0);
  const gstBreakdown = items.reduce((acc, it) => {
    const subtotal = (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 0);
    const gst = calcItemGst(subtotal, it.tax_rate, form.shipping_state);
    return {
      gst_type: gst.cgst > 0 ? 'intra-state' : gst.igst > 0 ? 'inter-state' : acc.gst_type,
      cgst: +(acc.cgst + gst.cgst).toFixed(2),
      sgst: +(acc.sgst + gst.sgst).toFixed(2),
      igst: +(acc.igst + gst.igst).toFixed(2),
      tax_amount: +(acc.tax_amount + gst.tax).toFixed(2),
      taxable: +(acc.taxable + gst.taxable).toFixed(2),
    };
  }, { gst_type: '', cgst: 0, sgst: 0, igst: 0, tax_amount: 0, taxable: 0 });

  const openCreate = () => {
    setEditing(null);
    setForm({ marketplace_account_id: '', marketplace_order_id: '', customer_name: '', customer_email: '', customer_phone: '', shipping_address: '', shipping_city: '', shipping_state: '', status: 'pending', payment_status: 'pending', order_date: new Date().toISOString().split('T')[0] });
    setItems([]);
    setSelectedProductId('');
    setShowForm(true);
  };

  const openEdit = (o) => {
    setEditing(o);
    setForm({
      marketplace_account_id: o.marketplace_account_id || '',
      marketplace_order_id: o.marketplace_order_id || '',
      customer_name: o.customer_name || '',
      customer_email: o.customer_email || '',
      customer_phone: o.customer_phone || '',
      shipping_address: o.shipping_address || '',
      shipping_city: o.shipping_city || '',
      shipping_state: o.shipping_state || '',
      status: o.status || 'pending',
      payment_status: o.payment_status || 'pending',
      order_date: (o.order_date || '').split(' ')[0]
    });
    setItems((o.items || []).map(it => ({
      product_id: it.product_id,
      product_name: it.product_name || '',
      quantity: it.quantity,
      unit_price: it.unit_price,
      tax_rate: it.tax_rate || 0,
    })));
    setSelectedProductId('');
    setShowForm(true);
  };

  const addItem = () => {
    if (!selectedProductId) return;
    const prod = products.find(p => String(p.id) === String(selectedProductId));
    if (!prod) return;
    const existing = items.findIndex(i => String(i.product_id) === String(selectedProductId));
    if (existing >= 0) {
      const updated = [...items];
      updated[existing] = { ...updated[existing], quantity: (parseInt(updated[existing].quantity) || 0) + 1 };
      setItems(updated);
    } else {
      setItems([...items, { product_id: prod.id, product_name: prod.name, quantity: 1, unit_price: prod.base_price, tax_rate: prod.tax_rate || 18 }]);
    }
    setSelectedProductId('');
  };

  const removeItem = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const updateItem = (idx, field, value) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: value };
    setItems(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { setError('Add at least one product'); return; }
    try {
      const payload = {
        ...form,
        items: items.map(it => ({
          product_id: it.product_id,
          quantity: parseInt(it.quantity) || 1,
          unit_price: parseFloat(it.unit_price) || 0,
        })),
        order_date: form.order_date + ' 00:00:00'
      };
      if (editing) { await api.post('/orders/update.php', { id: editing.id, ...payload }); }
      else { await api.post('/orders/create.php', payload); }
      setShowForm(false);
      setError('');
      fetchOrders(page.page);
    } catch (e) {
      const msg = e.response?.data?.message || 'Failed to save order';
      setError(msg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this order?')) return;
    try { await api.post('/orders/delete.php', { id }); fetchOrders(page.page); }
    catch (e) { setError('Failed to delete order'); }
  };

  const statusColor = (s) => {
    const map = { pending: 'bg-yellow-100 text-yellow-800', confirmed: 'bg-blue-100 text-blue-800', processing: 'bg-indigo-100 text-indigo-800', shipped: 'bg-purple-100 text-purple-800', delivered: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800', returned: 'bg-gray-100 text-gray-800' };
    return map[s?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const typeColors = { amazon: 'bg-orange-100 text-orange-800', flipkart: 'bg-blue-100 text-blue-800', meesho: 'bg-purple-100 text-purple-800' };

  if (loading && !orders.length) return <div className="text-center py-20 text-gray-600 dark:text-gray-400">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Orders</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Total: {page.total} orders</p>
        </div>
        <button onClick={openCreate} className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors">Add Order</button>
      </div>

      <div className="flex gap-2 items-center">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Platform:</label>
        <select value={filter} onChange={e => { setFilter(e.target.value); setPage(p => ({...p, page: 1})); }} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white">
          <option value="">All Platforms</option>
          <option value="amazon">Amazon</option>
          <option value="flipkart">Flipkart</option>
          <option value="meesho">Meesho</option>
        </select>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total (incl. GST)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">GST Breakdown</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {orders.length === 0 ? (
                <tr><td colSpan="9" className="px-6 py-12 text-center text-gray-500">No orders found.</td></tr>
              ) : orders.map(o => (
                <tr key={o.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${typeColors[o.marketplace_type] || 'bg-gray-100'}`}>{o.marketplace_type || 'N/A'}</span>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-blue-600">{o.marketplace_order_id || `#${o.id}`}</td>
                  <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                    {o.order_date ? new Date(o.order_date).toLocaleDateString('en-IN', {day:'2-digit', month:'short', year:'numeric'}) : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {o.customer_name || 'N/A'}
                    {o.shipping_city && <span className="block text-xs text-gray-400">{o.shipping_city}{o.shipping_state ? `, ${o.shipping_state}` : ''}</span>}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {o.items && o.items.length > 0 ? (
                      <span className="text-xs">{o.items.map(it => it.product_name || `#${it.product_id}`).join(', ')}</span>
                    ) : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold">₹{parseFloat(o.total_amount || 0).toFixed(2)}</td>
                  <td className="px-6 py-4 text-xs">
                    {o.taxable_amount ? (
                      <div className="space-y-0.5">
                        <span className="text-gray-500">Taxable: ₹{parseFloat(o.taxable_amount || 0).toFixed(2)}</span>
                        <span className={`block font-medium ${o.gst_type === 'intra-state' ? 'text-green-700' : 'text-purple-700'}`}>
                          {o.gst_type === 'intra-state'
                            ? `CGST ₹${parseFloat(o.cgst||0).toFixed(2)} + SGST ₹${parseFloat(o.sgst||0).toFixed(2)}`
                            : `IGST ₹${parseFloat(o.igst||0).toFixed(2)}`}
                        </span>
                      </div>
                    ) : '-'}
                  </td>
                  <td className="px-6 py-4"><span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColor(o.status)}`}>{o.status}</span></td>
                  <td className="px-6 py-4 text-sm space-x-2">
                    <button onClick={() => openEdit(o)} className="text-primary-600 hover:text-primary-900">Edit</button>
                    <button onClick={() => handleDelete(o.id)} className="text-red-600 hover:text-red-900">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {page.total_pages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 bg-white dark:bg-gray-800 rounded-lg shadow-md">
          <span className="text-sm text-gray-600">Page {page.page} of {page.total_pages}</span>
          <div className="flex gap-2">
            <button onClick={() => fetchOrders(page.page - 1)} disabled={page.page === 1} className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50">Previous</button>
            <button onClick={() => fetchOrders(page.page + 1)} disabled={page.page === page.total_pages} className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50">Next</button>
          </div>
        </div>
      )}

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit Order' : 'Add Order'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div><label className="block text-sm font-medium mb-1">Platform</label>
              <select value={form.marketplace_account_id} onChange={e => setForm({...form, marketplace_account_id: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500">
                <option value="">-- Select --</option>
                {platforms.map(p => (
                  <option key={p.id} value={p.id}>{p.account_name} ({p.marketplace_type})</option>
                ))}
              </select>
            </div>
            <div><label className="block text-sm font-medium mb-1">Order ID</label><input type="text" value={form.marketplace_order_id} onChange={e => setForm({...form, marketplace_order_id: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" /></div>
            <div><label className="block text-sm font-medium mb-1">Customer Name *</label><input type="text" value={form.customer_name} onChange={e => setForm({...form, customer_name: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required /></div>
            <div><label className="block text-sm font-medium mb-1">Order Date</label><input type="date" value={form.order_date} onChange={e => setForm({...form, order_date: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" /></div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Products</h3>
            <div className="flex gap-2 items-end mb-3">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Add Product</label>
                <select value={selectedProductId} onChange={e => setSelectedProductId(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500">
                  <option value="">-- Select Product --</option>
                  {products.filter(p => p.parent_id).length > 0 && (
                    <optgroup label="Variants">
                      {products.filter(p => p.parent_id).map(p => {
                        const parent = products.find(x => String(x.id) === String(p.parent_id));
                        const label = parent ? `${parent.name} > ${p.name}` : p.name;
                        return <option key={p.id} value={p.id}>{label} (₹{p.base_price}) - GST {p.tax_rate || 18}%</option>;
                      })}
                    </optgroup>
                  )}
                  {products.filter(p => !p.parent_id).length > 0 && (
                    <optgroup label="Main Products">
                      {products.filter(p => !p.parent_id).map(p => (
                        <option key={p.id} value={p.id}>{p.name} (₹{p.base_price}) - GST {p.tax_rate || 18}%</option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
              <button type="button" onClick={addItem} className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg whitespace-nowrap">+ Add</button>
            </div>

            {items.length > 0 && (
              <div className="overflow-x-auto mb-3">
                <table className="w-full text-sm border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Product</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Qty</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Unit Price</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Total</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">GST%</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {items.map((it, idx) => {
                      const sub = (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 0);
                      return (
                        <tr key={idx}>
                          <td className="px-3 py-2 font-medium">{it.product_name}</td>
                          <td className="px-3 py-2 text-center">
                            <input type="number" min="1" value={it.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} className="w-16 px-2 py-1 border border-gray-300 rounded text-center" />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <input type="number" step="0.01" min="0" value={it.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)} className="w-24 px-2 py-1 border border-gray-300 rounded text-right" />
                          </td>
                          <td className="px-3 py-2 text-right font-semibold">₹{sub.toFixed(2)}</td>
                          <td className="px-3 py-2 text-center">{it.tax_rate}%</td>
                          <td className="px-3 py-2 text-right">
                            <button type="button" onClick={() => removeItem(idx)} className="text-red-500 hover:text-red-700 text-lg leading-none">&times;</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50 font-semibold">
                    <tr>
                      <td colSpan="3" className="px-3 py-2 text-right">Total:</td>
                      <td className="px-3 py-2 text-right">₹{totalAmount.toFixed(2)}</td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Shipping Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3"><label className="block text-sm font-medium mb-1">Address</label><textarea value={form.shipping_address} onChange={e => setForm({...form, shipping_address: e.target.value})} rows="2" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"></textarea></div>
              <div><label className="block text-sm font-medium mb-1">City</label><input type="text" value={form.shipping_city} onChange={e => setForm({...form, shipping_city: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" /></div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">State</label>
                <select value={form.shipping_state} onChange={e => setForm({...form, shipping_state: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500">
                  <option value="">Select State</option>
                  {IND_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>

          {items.length > 0 && form.shipping_state && (
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-semibold mb-2">GST Breakdown (Prices include GST)</h4>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">Type</span>
                  <p className={`font-semibold ${gstBreakdown.gst_type === 'intra-state' ? 'text-green-600' : 'text-purple-600'}`}>
                    {gstBreakdown.gst_type === 'intra-state' ? 'Intra-State' : gstBreakdown.gst_type === 'inter-state' ? 'Inter-State' : '-'}
                  </p>
                </div>
                <div><span className="text-gray-500">Taxable Value</span><p className="font-semibold text-gray-800">₹{gstBreakdown.taxable.toFixed(2)}</p></div>
                {gstBreakdown.gst_type === 'intra-state' && (
                  <>
                    <div><span className="text-gray-500">CGST</span><p className="font-semibold text-green-600">₹{gstBreakdown.cgst.toFixed(2)}</p></div>
                    <div><span className="text-gray-500">SGST</span><p className="font-semibold text-green-600">₹{gstBreakdown.sgst.toFixed(2)}</p></div>
                  </>
                )}
                {gstBreakdown.gst_type === 'inter-state' && (
                  <div><span className="text-gray-500">IGST</span><p className="font-semibold text-purple-600">₹{gstBreakdown.igst.toFixed(2)}</p></div>
                )}
                <div><span className="text-gray-500">Total GST</span><p className="font-semibold text-gray-800">₹{gstBreakdown.tax_amount.toFixed(2)}</p></div>
                <div><span className="text-gray-500">Total (incl. GST)</span><p className="font-semibold text-lg text-gray-800">₹{totalAmount.toFixed(2)}</p></div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Email</label><input type="email" value={form.customer_email} onChange={e => setForm({...form, customer_email: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" /></div>
            <div><label className="block text-sm font-medium mb-1">Phone</label><input type="text" value={form.customer_phone} onChange={e => setForm({...form, customer_phone: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" /></div>
            <div><label className="block text-sm font-medium mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500">
                <option value="pending">Pending</option><option value="confirmed">Confirmed</option><option value="processing">Processing</option><option value="shipped">Shipped</option><option value="delivered">Delivered</option><option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div><label className="block text-sm font-medium mb-1">Payment</label>
              <select value={form.payment_status} onChange={e => setForm({...form, payment_status: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500">
                <option value="pending">Pending</option><option value="paid">Paid</option><option value="failed">Failed</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
            <button type="submit" className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg">{editing ? 'Update' : 'Create'} Order</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Orders;
